"use client";

// Libraries
import Image from "next/image";
import Button from "../../Button/Button";
import Icon from "../../Icon/Icon";
import { useEffect, useState } from "react";
import Link from "next/link";
import { SpinnerCircular, SpinnerDotted } from "spinners-react";
import { signOut, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import "moment/locale/fr";
import NavItem from "../NavItem/NavItem";
import Nprogress from "nprogress";
import Logo from "../../Logo/Logo";

export default function NewHeader({ page, ...props }) {
    // Variables
    const { data: session, status } = useSession();
    const loading = status === "loading";
    const { pathname } = useRouter();

    // States
    const [isScrolled, setIsScrolled] = useState(false);
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [notificationsPanelIsOpen, setNotificationsPanelIsOpen] =
        useState(false);
    const [notificationIsDeleting, setNotificationIsDeleting] = useState(false);
    const [notificationsAreLoading, setNotificationsAreLoading] =
        useState(false);
    const [notifications, setNotifications] = useState([]);
    const [sideIsOpen, setSideIsOpen] = useState(false);

    // Methods
    const onSignOutClickedHandler = () => {
        toast.success(`À bientôt ${session.user.firstName}`);

        // if actual page is /articles or /r/:slug
        if (
            pathname == "/articles" ||
            pathname == "/r/[slug]" ||
            pathname == "/courses" ||
            pathname == "/course/[slug]"
        ) {
            signOut({ redirect: false });
        } else {
            signOut({ callbackUrl: "/" });
        }
    };
    const onToggleDarkMode = async () => {
        if (!isLoading) {
            setIsLoading(true);
            Nprogress.start();

            const response = await fetch("/api/user/toggle-dark-mode", {
                method: "get",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                Nprogress.done();
                setIsLoading(false);
                toast.error(fetchedData.message);
            } else {
                Nprogress.done();
                setIsLoading(false);
                const event = new Event("visibilitychange");
                document.dispatchEvent(event);
            }

            setIsLoading(false);
        }
    };
    const loadNotifications = async (markAsRead = false) => {
        if (!notificationsAreLoading) {
            setNotificationsAreLoading(true);
            const response = await fetch("/api/user/get/notifications", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ markAsRead }),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setNotificationsAreLoading(false);
                toast.error(fetchedData.message);
            } else {
                setNotificationsAreLoading(false);
                setNotifications(fetchedData.notifications);

                if (markAsRead) {
                    const event = new Event("visibilitychange");
                    document.dispatchEvent(event);
                }
            }
        }
    };
    const onDeleteNotificationHandler = async (notificationId) => {
        if (!notificationIsDeleting) {
            setNotificationIsDeleting(true);
            const response = await fetch("/api/user/delete/notification", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    notificationId,
                }),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setNotificationIsDeleting(false);
                toast.error(fetchedData.message);
            } else {
                setNotificationIsDeleting(false);
                setNotifications(
                    notifications.filter(
                        (notification) => notification.id !== notificationId,
                    ),
                );
                toast.success("Notification supprimée.");
            }
        }
    };

    // Cycles
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 0 && !props.unstickyHeader) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener("scroll", handleScroll);

        loadNotifications();

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);
    useEffect(() => {
        document.body.style.overflow = sideIsOpen ? "hidden" : "auto";
    }, [sideIsOpen]);

    let links = null;
    if (session) {
        links = (
            <ul>
                {session &&
                    session.user &&
                    session.user.roles.includes("ROLE_SUPER_ADMIN") && (
                        <Link
                            href="/admin"
                            className="text-black hover:text-white"
                        >
                            <li className="dropdown-item flex items-center gap-3">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                                    />
                                </svg>
                                Administration
                            </li>
                        </Link>
                    )}
                {(session.user.roles.includes("ROLE_SUPER_ADMIN") ||
                    session.user.roles.includes("ROLE_ADMIN") ||
                    session.user.roles.includes("ROLE_INSTRUCTOR")) && (
                    <Link
                        href="/instructor"
                        className="text-black hover:text-white"
                    >
                        <li className="dropdown-item flex items-center gap-3">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="currentColor"
                                    fillRule="evenodd"
                                    d="M18.059 4.708a2.25 2.25 0 0 0-2.166-1.25a2.952 2.952 0 0 0-.51.096l-.446 3.74l3.118-.836a8.175 8.175 0 0 0 .289-.083a.25.25 0 0 0 .137-.238a8.315 8.315 0 0 0-.073-.292c-.19-.708-.26-.955-.35-1.137Zm-4.683 3.003l.447-3.743l-4.719 1.264l-.446 3.743l4.718-1.264ZM7.098 9.394l.446-3.743c-.817.219-1.401.379-1.85.544c-.474.174-.728.33-.904.505c-.279.28-.48.628-.582 1.009c-.064.24-.072.537.014 1.034c.063.362.167.79.31 1.338l2.566-.687ZM3.25 11.093c-.226-.843-.408-1.526-.506-2.094c-.104-.6-.129-1.141.015-1.678a3.75 3.75 0 0 1 .97-1.681c.394-.393.875-.643 1.447-.853c.554-.203 1.259-.392 2.132-.626l.78-.21a.746.746 0 0 1 .286-.076l5.972-1.6l.021-.006a.746.746 0 0 1 .286-.076c.474-.126.821-.21 1.142-.232a3.75 3.75 0 0 1 3.609 2.084c.164.332.272.736.43 1.328l.023.084l.012.046c.048.176.097.359.109.534a1.75 1.75 0 0 1-.973 1.684a2.826 2.826 0 0 1-.562.186L9.697 10.25h6.355c.898 0 1.648 0 2.242.08c.628.084 1.195.27 1.65.725c.456.456.642 1.023.726 1.65c.08.595.08 1.345.08 2.243v1.107c0 1.367 0 2.47-.117 3.337c-.12.9-.38 1.658-.982 2.26c-.602.601-1.36.86-2.26.981c-.866.117-1.969.117-3.336.117h-4.11c-1.368 0-2.47 0-3.337-.117c-.9-.12-1.658-.38-2.26-.981c-.602-.602-.86-1.36-.982-2.26c-.116-.867-.116-1.97-.116-3.337v-4.962Zm1.5.657V16c0 1.435.001 2.436.103 3.192c.099.734.28 1.122.556 1.399c.277.277.665.457 1.399.556c.755.101 1.756.103 3.192.103h4c1.435 0 2.436-.002 3.192-.103c.733-.099 1.122-.28 1.399-.556c.277-.277.457-.665.556-1.4c.101-.755.103-1.756.103-3.191v-1c0-.964-.002-1.612-.067-2.095c-.062-.461-.169-.659-.3-.789c-.13-.13-.327-.237-.789-.3c-.483-.065-1.13-.066-2.094-.066H4.75Zm8.448 2.83l.047.034l.042.03c.361.259.7.501.94.738c.27.265.523.623.523 1.118c0 .495-.252.852-.522 1.118c-.241.237-.58.48-.94.738l-.043.03l-.047.034c-.361.26-.706.506-1.001.652c-.315.155-.834.321-1.33-.029c-.43-.304-.533-.795-.574-1.138c-.043-.358-.043-.826-.043-1.358v-.094c0-.533 0-1 .043-1.358c.04-.343.144-.834.575-1.138c.495-.35 1.014-.185 1.329-.03c.295.146.64.394 1.001.653Zm-1.429.832c-.018.25-.02.593-.02 1.088c0 .495.002.838.02 1.088c.149-.097.34-.234.602-.421c.418-.3.658-.474.805-.619a.62.62 0 0 0 .046-.048a1.009 1.009 0 0 0-.046-.048c-.147-.145-.387-.32-.805-.619a18.297 18.297 0 0 0-.602-.421Z"
                                    clipRule="evenodd"
                                ></path>
                            </svg>
                            Panel de formateur
                        </li>
                    </Link>
                )}
                {(session.user.roles.includes("ROLE_SUPER_ADMIN") ||
                    session.user.roles.includes("ROLE_ADMIN") ||
                    session.user.roles.includes("ROLE_MENTOR")) && (
                    <Link
                        href="/mentor"
                        className="text-black hover:text-white"
                    >
                        <li className="dropdown-item flex items-center gap-3">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
                                />
                            </svg>
                            Panel de mentorat
                        </li>
                    </Link>
                )}
                {session.user.hasVerifiedEmail && (
                    <Link
                        href="/writer"
                        className="text-black hover:text-white"
                    >
                        <li className="dropdown-item flex items-center gap-3">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="currentColor"
                                    fillRule="evenodd"
                                    d="M11.943 1.25h.114c2.309 0 4.118 0 5.53.19c1.444.194 2.584.6 3.479 1.494c.793.793 1.203 1.78 1.42 3.005c.215 1.203.254 2.7.262 4.558a.75.75 0 0 1-1.5.006a73.32 73.32 0 0 0-.025-1.753H7.011a.73.73 0 0 1-.024 0h-4.21A120.56 120.56 0 0 0 2.75 12c0 2.378.002 4.086.176 5.386c.172 1.279.5 2.05 1.069 2.62c.484.484 1.112.79 2.067.978c.976.19 2.235.246 3.944.26a.75.75 0 1 1-.012 1.5c-1.704-.014-3.092-.067-4.22-.288c-1.15-.226-2.084-.634-2.84-1.39c-.895-.895-1.3-2.035-1.494-3.48c-.19-1.411-.19-3.22-.19-5.529v-.114c0-2.309 0-4.118.19-5.53c.194-1.444.6-2.584 1.494-3.479c.895-.895 2.035-1.3 3.48-1.494c1.411-.19 3.22-.19 5.529-.19Zm-9.086 6h3.731l2.856-4.488c-1.127.017-2.052.06-2.83.164c-1.279.172-2.05.5-2.62 1.069c-.569.57-.896 1.34-1.068 2.619c-.027.202-.05.413-.07.636Zm8.35-4.5a.753.753 0 0 1-.074.153L8.366 7.25h4.722l2.83-4.448C14.857 2.75 13.576 2.75 12 2.75h-.793Zm6.391.207L14.866 7.25h6.277c-.033-.382-.076-.73-.133-1.048c-.184-1.036-.498-1.7-1.005-2.207c-.537-.538-1.254-.86-2.407-1.038Zm.85 10.031a2.52 2.52 0 1 1 3.564 3.563l-4.282 4.282a6.38 6.38 0 0 1-.572.532a3.698 3.698 0 0 1-.683.423c-.206.098-.422.17-.732.273l-1.878.626a1.227 1.227 0 0 1-1.552-1.552l.626-1.878c.103-.31.175-.526.273-.732c.116-.242.258-.472.423-.683c.14-.18.301-.341.532-.572l4.282-4.282Zm2.503 1.06a1.02 1.02 0 0 0-1.442 0l-.131.132l.016.05c.082.236.238.548.533.843a2.223 2.223 0 0 0 .893.55l.131-.132a1.02 1.02 0 0 0 0-1.442Zm-1.265 2.708a3.756 3.756 0 0 1-1.442-1.442L15.258 18.3c-.272.273-.364.366-.44.464a2.193 2.193 0 0 0-.252.406c-.053.113-.096.236-.218.601l-.345 1.037l.189.19l1.037-.347c.365-.121.488-.164.6-.217a2.21 2.21 0 0 0 .407-.252c.098-.076.191-.168.464-.44l2.986-2.986Z"
                                    clipRule="evenodd"
                                ></path>
                            </svg>
                            Panel de rédacteur
                        </li>
                    </Link>
                )}
                <Link href="/account" className="text-black hover:text-white">
                    <li className="dropdown-item flex items-center gap-3">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                            />
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                        </svg>
                        Paramètres
                    </li>
                </Link>
                {session && session.user && session.user.darkMode ? (
                    <li
                        onClick={onToggleDarkMode}
                        className="dropdown-item flex items-center gap-3 hover:text-white"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                            />
                        </svg>
                        Allumer la lumière
                    </li>
                ) : (
                    <li
                        onClick={onToggleDarkMode}
                        className="dropdown-item flex items-center gap-3 hover:text-white"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                            />
                        </svg>
                        Eteindre la lumière
                    </li>
                )}
                <li
                    onClick={onSignOutClickedHandler}
                    className="dropdown-item flex items-center gap-3 hover:text-white"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                    </svg>
                    Déconnexion
                </li>
            </ul>
        );
    }

    const notificationsPanel = (
        <div className="rounded bg-white shadow-xl md:w-80 max-h-96 overflow-auto grid grid-cols-1 divide-y divide-gray-200">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                className="text-white absolute -top-5 right-0 w-8 h-8"
                viewBox="0 0 24 24"
            >
                <path
                    fill="currentColor"
                    d="m12.37 8.165l6.43 6.63c.401.414.158 1.205-.37 1.205H5.57c-.528 0-.771-.79-.37-1.205l6.43-6.63a.499.499 0 0 1 .74 0Z"
                ></path>
            </svg>
            {/* If notifications are loading */}
            {notificationsAreLoading && !notifications[0] && (
                <div className="flex justify-center items-center h-full p-5">
                    <SpinnerCircular
                        size={45}
                        thickness={300}
                        color="#C4A461"
                    />
                </div>
            )}
            {/* Notification */}
            {notifications.map((notification) => (
                <Link
                    href={notification.link || "/"}
                    key={notification.id}
                    className="text-black hover:text-black"
                >
                    <div
                        className={`p-5 group cursor-pointer hover:bg-gray-300 ${
                            !notification.seen && "bg-gray-100"
                        }`}
                    >
                        {/* Cross */}
                        <div
                            className="absolute right-5 transform scale-0 group-hover:scale-100 duration-150 bg-primary hover:bg-primary-300 text-white rounded"
                            onMouseDown={() =>
                                onDeleteNotificationHandler(notification.id)
                            }
                        >
                            {notificationIsDeleting ? (
                                <SpinnerCircular
                                    size={24}
                                    thickness={100}
                                    speed={100}
                                    color="#613bdb"
                                />
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-6 w-6"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            )}
                        </div>
                        {/* Content */}
                        {notification.message}
                        {/* Date */}
                        <div className="text-sm text-textSecondDark">
                            {moment(notification.dateCreation)
                                .calendar()[0]
                                .toUpperCase() +
                                moment(notification.dateCreation)
                                    .calendar()
                                    .slice(1)}
                        </div>
                    </div>
                </Link>
            ))}
        </div>
    );

    return (
        <div className={`${props.blackHeader && "dark"}`}>
            {/* Side */}
            <div
                className={`top-0 left-0 bottom-0 right-0 w-full h-full transition-all duration-150 ease-in-out bg-black cursor-pointer ${
                    sideIsOpen ? "bg-opacity-50 fixed" : "bg-opacity-0 absolute"
                }`}
                style={{ zIndex: sideIsOpen ? 1000 : -100 }}
                onClick={() => setSideIsOpen(false)}
            >
                <div className="w-full h-full background-blur"></div>
                <div
                    className={`fixed top-0 left-0 bottom-0 h-full transition-all duration-150 ease-in-out bg-white dark:bg-[#202329] w-80 cursor-auto ${
                        sideIsOpen ? "ml-0" : "-ml-80"
                    }`}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        boxShadow:
                            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
                    }}
                >
                    <div className="flex flex-col items-center h-full pt-10 overflow-y-auto relative">
                        <div
                            className="absolute top-5 right-5 "
                            onClick={(e) => {
                                e.stopPropagation();
                                setSideIsOpen(false);
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="text-primary hover:text-primary-300 w-7 h-7 cursor-pointer"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="none"
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.5"
                                    d="M20 12H4m0 0l6-6m-6 6l6 6"
                                ></path>
                            </svg>
                        </div>
                        {/* Logo */}
                        <div className="hidden dark:block">
                            <Logo white />
                        </div>
                        <div className="block dark:hidden">
                            <Logo />
                        </div>
                        <div className="flex flex-col pt-10 w-full h-full">
                            {/* Links */}
                            <div className="flex-1">
                                <Link href="/">
                                    <NavItem forPage="home" page={page}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-7 h-7"
                                            viewBox="0 0 24 24"
                                        >
                                            {page == "home" ? (
                                                <path
                                                    fill="currentColor"
                                                    fillRule="evenodd"
                                                    d="M2.52 7.823C2 8.77 2 9.915 2 12.203v1.522c0 3.9 0 5.851 1.172 7.063C4.343 22 6.229 22 10 22h4c3.771 0 5.657 0 6.828-1.212C22 19.576 22 17.626 22 13.725v-1.521c0-2.289 0-3.433-.52-4.381c-.518-.949-1.467-1.537-3.364-2.715l-2-1.241C14.111 2.622 13.108 2 12 2c-1.108 0-2.11.622-4.116 1.867l-2 1.241C3.987 6.286 3.038 6.874 2.519 7.823Zm6.927 7.575a.75.75 0 1 0-.894 1.204A5.766 5.766 0 0 0 12 17.75a5.766 5.766 0 0 0 3.447-1.148a.75.75 0 1 0-.894-1.204A4.267 4.267 0 0 1 12 16.25a4.267 4.267 0 0 1-2.553-.852Z"
                                                    clipRule="evenodd"
                                                ></path>
                                            ) : (
                                                <g fill="currentColor">
                                                    <path d="M9.447 15.398a.75.75 0 1 0-.894 1.204A5.766 5.766 0 0 0 12 17.75a5.766 5.766 0 0 0 3.447-1.148a.75.75 0 1 0-.894-1.204A4.267 4.267 0 0 1 12 16.25a4.267 4.267 0 0 1-2.553-.852Z"></path>
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M12 1.25c-.708 0-1.351.203-2.05.542c-.674.328-1.454.812-2.427 1.416L5.456 4.491c-.92.572-1.659 1.03-2.227 1.465c-.589.45-1.041.91-1.368 1.507c-.326.595-.472 1.229-.543 1.978c-.068.725-.068 1.613-.068 2.726v1.613c0 1.904 0 3.407.153 4.582c.156 1.205.486 2.178 1.23 2.947c.747.773 1.697 1.119 2.875 1.282c1.14.159 2.598.159 4.434.159h4.116c1.836 0 3.294 0 4.434-.159c1.177-.163 2.128-.509 2.876-1.282c.743-.769 1.073-1.742 1.23-2.947c.152-1.175.152-2.678.152-4.582v-1.613c0-1.113 0-2-.068-2.726c-.07-.75-.217-1.383-.543-1.978c-.327-.597-.78-1.056-1.368-1.507c-.568-.436-1.306-.893-2.227-1.465l-2.067-1.283c-.973-.604-1.753-1.088-2.428-1.416c-.697-.34-1.34-.542-2.049-.542ZM8.28 4.504c1.015-.63 1.73-1.072 2.327-1.363c.581-.283.993-.391 1.393-.391s.812.108 1.393.391c.598.29 1.312.733 2.327 1.363l2 1.241c.961.597 1.636 1.016 2.14 1.402c.489.375.77.684.963 1.036c.193.353.306.766.365 1.398c.061.648.062 1.465.062 2.623v1.521c0 1.97-.002 3.376-.14 4.443c-.136 1.048-.393 1.656-.82 2.099c-.425.439-1.003.7-2.004.839c-1.026.142-2.379.144-4.286.144h-4c-1.908 0-3.26-.002-4.286-.144c-1.001-.14-1.579-.4-2.003-.84c-.428-.442-.685-1.05-.82-2.098c-.14-1.067-.141-2.472-.141-4.443v-1.521c0-1.158 0-1.975.062-2.623c.059-.632.172-1.045.365-1.398c.193-.352.474-.661.964-1.036c.503-.386 1.178-.805 2.139-1.402l2-1.24Z"
                                                        clipRule="evenodd"
                                                    ></path>
                                                </g>
                                            )}
                                        </svg>
                                        Accueil
                                    </NavItem>
                                </Link>
                                <Link href="/articles">
                                    <NavItem forPage="articles" page={page}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-7 h-7"
                                            viewBox="0 0 24 24"
                                        >
                                            {page == "articles" ? (
                                                <g fill="currentColor">
                                                    <path d="M8.51 2h6.98c.232 0 .41 0 .566.015c1.108.109 2.015.775 2.4 1.672H5.544c.385-.897 1.292-1.563 2.4-1.672C8.098 2 8.276 2 8.51 2Zm-2.2 2.723c-1.39 0-2.53.84-2.91 1.954a2.587 2.587 0 0 0-.024.07c.398-.12.813-.2 1.232-.253c1.08-.139 2.446-.139 4.032-.139h6.892c1.586 0 2.951 0 4.032.139c.42.054.834.132 1.232.253a2.173 2.173 0 0 0-.023-.07c-.38-1.114-1.52-1.954-2.911-1.954H6.31Z"></path>
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M8.672 7.542h6.656c3.374 0 5.062 0 6.01.987c.947.987.724 2.511.278 5.56l-.422 2.892c-.35 2.391-.525 3.587-1.422 4.303c-.897.716-2.22.716-4.867.716h-5.81c-2.646 0-3.97 0-4.867-.716c-.897-.716-1.072-1.912-1.422-4.303l-.422-2.891c-.447-3.05-.67-4.574.278-5.561c.948-.987 2.636-.987 6.01-.987ZM8 18c0-.414.373-.75.833-.75h6.334c.46 0 .833.336.833.75s-.373.75-.833.75H8.833c-.46 0-.833-.336-.833-.75Z"
                                                        clipRule="evenodd"
                                                    ></path>
                                                </g>
                                            ) : (
                                                <g
                                                    fill="currentColor"
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                >
                                                    <path d="M8.25 18a.75.75 0 0 1 .75-.75h6a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1-.75-.75Z"></path>
                                                    <path d="M8.7 1.25h6.6c.22 0 .389 0 .536.016A2.75 2.75 0 0 1 18.29 3.87a2.888 2.888 0 0 1 2.054 2.721c.601.18 1.12.465 1.544.923c.652.705.854 1.572.862 2.586c.007.975-.166 2.207-.382 3.736l-.44 3.114c-.168 1.196-.305 2.168-.518 2.929c-.223.797-.552 1.452-1.16 1.956c-.604.5-1.32.715-2.166.817c-.819.098-1.849.098-3.13.098H9.046c-1.282 0-2.312 0-3.13-.098c-.847-.102-1.563-.317-2.167-.817c-.608-.504-.937-1.16-1.16-1.956c-.213-.761-.35-1.733-.519-2.93l-.439-3.113c-.215-1.53-.39-2.761-.382-3.736c.008-1.014.21-1.881.862-2.586c.424-.458.942-.742 1.543-.923a2.888 2.888 0 0 1 2.055-2.72a2.75 2.75 0 0 1 2.454-2.605c.147-.016.316-.016.535-.016ZM5.188 6.328c.926-.078 2.06-.078 3.427-.078h6.768c1.366 0 2.5 0 3.427.078a1.383 1.383 0 0 0-1.35-1.078H6.539c-.669 0-1.212.47-1.349 1.078Zm10.487-3.57c.55.058.985.468 1.092.992H7.232a1.25 1.25 0 0 1 1.092-.993c.056-.006.136-.007.417-.007h6.518c.28 0 .36.001.417.007ZM3.213 8.532c.303-.327.758-.544 1.643-.662c.901-.12 2.108-.121 3.816-.121h6.656c1.708 0 2.915.002 3.816.121c.885.118 1.34.335 1.643.662c.296.32.457.755.463 1.579c.006.85-.15 1.97-.376 3.576l-.423 3c-.178 1.261-.302 2.133-.485 2.787c-.177.63-.384.965-.673 1.204c-.293.244-.687.4-1.388.484c-.719.086-1.659.087-3 .087h-5.81c-1.342 0-2.281-.001-3-.087c-.7-.085-1.095-.24-1.388-.483c-.289-.24-.496-.576-.673-1.205c-.183-.654-.307-1.526-.485-2.787l-.423-3c-.226-1.605-.382-2.726-.376-3.576c.006-.824.167-1.26.463-1.579Z"></path>
                                                </g>
                                            )}
                                        </svg>
                                        Articles
                                    </NavItem>
                                </Link>
                                <Link href="/courses">
                                    <NavItem forPage="courses" page={page}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-7 h-7"
                                            viewBox="0 0 24 24"
                                        >
                                            {page == "courses" ? (
                                                <g fill="currentColor">
                                                    <path d="M8.51 2h6.98c.232 0 .41 0 .566.015c1.108.109 2.015.775 2.4 1.672H5.544c.385-.897 1.292-1.563 2.4-1.672C8.098 2 8.276 2 8.51 2Zm-2.2 2.723c-1.39 0-2.53.84-2.91 1.954a2.587 2.587 0 0 0-.024.07c.398-.12.813-.2 1.232-.253c1.08-.139 2.446-.139 4.032-.139h6.892c1.586 0 2.951 0 4.032.139c.42.054.834.132 1.232.253a2.173 2.173 0 0 0-.023-.07c-.38-1.114-1.52-1.954-2.911-1.954H6.31Z"></path>
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M15.328 7.542H8.672c-3.374 0-5.062 0-6.01.987c-.948.987-.725 2.511-.278 5.56l.422 2.892c.35 2.391.525 3.587 1.422 4.303c.898.716 2.22.716 4.867.716h5.81c2.646 0 3.97 0 4.867-.716c.897-.716 1.072-1.912 1.422-4.303l.422-2.891c.447-3.05.67-4.574-.278-5.561c-.948-.987-2.636-.987-6.01-.987Zm-.747 8.252c.559-.346.559-1.242 0-1.588l-3.371-2.09c-.543-.337-1.21.101-1.21.794v4.18c0 .693.667 1.13 1.21.794l3.371-2.09Z"
                                                        clipRule="evenodd"
                                                    ></path>
                                                </g>
                                            ) : (
                                                <g
                                                    fill="currentColor"
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                >
                                                    <path d="M9.99 10.91a1.485 1.485 0 0 1 1.615-.022l3.371 2.09c.538.334.774.91.774 1.432c0 .523-.236 1.099-.774 1.432l-3.371 2.09c-.54.334-1.157.28-1.615-.022a1.673 1.673 0 0 1-.74-1.41v-4.18c0-.593.289-1.114.74-1.41Zm.823 1.254c-.019.012-.063.056-.063.156v4.18c0 .1.044.144.063.156l.001.001l3.372-2.09c.021-.013.064-.059.064-.157s-.043-.143-.064-.157l-3.371-2.09h-.002Z"></path>
                                                    <path d="M8.7 1.25c-.22 0-.39 0-.536.016A2.75 2.75 0 0 0 5.71 3.87a2.888 2.888 0 0 0-2.055 2.721c-.6.18-1.119.465-1.543.923c-.652.705-.854 1.572-.862 2.586c-.007.975.167 2.207.382 3.736l.44 3.114c.168 1.196.305 2.168.518 2.929c.223.797.552 1.452 1.16 1.956c.604.5 1.32.715 2.166.817c.819.098 1.849.098 3.13.098h5.907c1.282 0 2.312 0 3.13-.098c.847-.102 1.563-.317 2.167-.817c.608-.504.937-1.16 1.16-1.956c.213-.761.35-1.733.519-2.93l.439-3.113c.216-1.53.39-2.761.382-3.736c-.008-1.014-.21-1.881-.862-2.586c-.424-.458-.943-.742-1.544-.923a2.888 2.888 0 0 0-2.054-2.72a2.75 2.75 0 0 0-2.454-2.605c-.147-.016-.316-.016-.536-.016H8.7Zm10.11 5.078a1.383 1.383 0 0 0-1.348-1.078H6.538c-.669 0-1.212.47-1.349 1.078c.926-.078 2.06-.078 3.427-.078h6.768c1.366 0 2.5 0 3.427.078ZM16.769 3.75a1.25 1.25 0 0 0-1.092-.993a4.924 4.924 0 0 0-.417-.007H8.74c-.28 0-.361.001-.417.007a1.25 1.25 0 0 0-1.092.993h9.536ZM3.213 8.533c.303-.327.758-.544 1.643-.662c.901-.12 2.108-.121 3.816-.121h6.656c1.708 0 2.915.002 3.816.121c.885.118 1.34.335 1.643.662c.296.32.457.755.463 1.579c.006.85-.15 1.97-.376 3.576l-.423 3c-.178 1.261-.302 2.133-.485 2.787c-.177.63-.384.965-.673 1.204c-.293.244-.687.4-1.388.484c-.719.086-1.659.087-3 .087h-5.81c-1.342 0-2.281-.001-3-.087c-.7-.085-1.095-.24-1.388-.483c-.289-.24-.496-.576-.673-1.205c-.183-.654-.307-1.526-.485-2.787l-.423-3c-.226-1.605-.382-2.726-.376-3.576c.006-.824.167-1.26.463-1.579Z"></path>
                                                </g>
                                            )}
                                        </svg>
                                        Formations
                                    </NavItem>
                                </Link>
                                <hr className="border-gray-300 dark:border-bgDark-550 m-5" />
                                <Link href="/incubator">
                                    <NavItem forPage="incubator" page={page}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-7 h-7"
                                            viewBox="0 0 24 24"
                                        >
                                            {page == "incubator" ? (
                                                <path
                                                    fill="currentColor"
                                                    fillRule="evenodd"
                                                    d="M3.313 14.7a.75.75 0 0 1 .988-.387l5 2.188a.75.75 0 1 1-.601 1.374l-5-2.188a.75.75 0 0 1-.387-.987Zm17.374 0a.75.75 0 0 1-.386.987l-16 7a.75.75 0 0 1-.601-1.374l16-7a.75.75 0 0 1 .987.387Zm-6.874 4.593a.75.75 0 0 1 .988-.386l5.5 2.406a.75.75 0 1 1-.601 1.374l-5.5-2.406a.75.75 0 0 1-.387-.988ZM11.6 15c2.133 0 6.4-1.239 6.4-6.193c0-3.031-1.849-5.23-3.6-6.542c-.897-.674-2.047.025-2.047 1.124c0 .743-.21 1.977-.793 2.891c-.682 1.07-1.809.141-1.987-1.104c-.09-.624-.777-.882-1.312-.523C7.208 5.358 6 6.71 6 8.807C6 13.761 9.733 15 11.6 15Zm3.616-5.718a.75.75 0 0 1 .502.934c-.268.896-1.384 2.534-3.718 2.534a.75.75 0 0 1 0-1.5c1.506 0 2.15-1.028 2.282-1.466a.75.75 0 0 1 .934-.502Z"
                                                    clipRule="evenodd"
                                                ></path>
                                            ) : (
                                                <path
                                                    fill="currentColor"
                                                    fillRule="evenodd"
                                                    d="M12.669 1.51a1.962 1.962 0 0 1 2.182.155c1.848 1.385 3.9 3.78 3.9 7.142c0 2.732-1.196 4.506-2.722 5.57c-1.487 1.036-3.247 1.373-4.429 1.373a6.594 6.594 0 0 1-3.967-1.402c-1.356-1.08-2.383-2.85-2.383-5.54c0-2.424 1.406-3.982 2.594-4.778c.92-.618 2.292-.216 2.472 1.04c.065.46.302.787.472.898c.02.013.034.02.045.025a.498.498 0 0 0 .095-.116c.482-.757.675-1.838.675-2.488c0-.82.433-1.517 1.066-1.879Zm-1.856 4.496l.005-.002l-.005.002Zm.036-.008a.01.01 0 0 1 .003.001h-.003Zm2.563-3.184a.647.647 0 0 0-.309.575c0 .835-.228 2.223-.91 3.294c-.248.388-.598.687-1.049.784c-.447.096-.86-.036-1.176-.243c-.61-.398-1.025-1.156-1.137-1.941a.106.106 0 0 0-.007-.027L8.82 5.25s-.002-.003-.01-.006c-.013-.005-.061-.016-.132.032c-.917.615-1.929 1.76-1.929 3.531c0 2.263.84 3.59 1.817 4.368A5.097 5.097 0 0 0 11.6 14.25c.952 0 2.392-.282 3.572-1.104c1.14-.794 2.078-2.117 2.078-4.339c0-2.7-1.646-4.703-3.299-5.942c-.2-.15-.387-.138-.54-.051Zm1.804 6.468a.75.75 0 0 1 .503.934c-.27.896-1.384 2.534-3.719 2.534a.75.75 0 0 1 0-1.5c1.506 0 2.15-1.028 2.282-1.466a.75.75 0 0 1 .934-.502ZM3.313 14.699a.75.75 0 0 1 .988-.386l5 2.187a.75.75 0 1 1-.601 1.375l-5-2.188a.75.75 0 0 1-.387-.988Zm17.374 0a.75.75 0 0 1-.386.988l-16 7a.75.75 0 0 1-.601-1.374l16-7a.75.75 0 0 1 .987.386Zm-6.874 4.594a.75.75 0 0 1 .988-.386l5.5 2.406a.75.75 0 1 1-.601 1.374l-5.5-2.406a.75.75 0 0 1-.387-.988Z"
                                                    clipRule="evenodd"
                                                ></path>
                                            )}
                                        </svg>
                                        Incubateur
                                    </NavItem>
                                </Link>
                                <Link href="/accelerators">
                                    <NavItem forPage="accelerators" page={page}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-7 h-7"
                                            viewBox="0 0 24 24"
                                        >
                                            {page == "accelerators" ? (
                                                <path
                                                    fill="currentColor"
                                                    d="m17.578 4.432l-2-1.05C13.822 2.461 12.944 2 12 2s-1.822.46-3.578 1.382l-.321.169l8.923 5.099l4.016-2.01c-.646-.732-1.688-1.279-3.462-2.21Zm4.17 3.532l-3.998 2V13a.75.75 0 0 1-1.5 0v-2.287l-3.5 1.75v9.441c.718-.179 1.535-.607 2.828-1.286l2-1.05c2.151-1.129 3.227-1.693 3.825-2.708c.597-1.014.597-2.277.597-4.8v-.117c0-1.893 0-3.076-.252-3.978ZM11.25 21.904v-9.44l-8.998-4.5C2 8.866 2 10.05 2 11.941v.117c0 2.525 0 3.788.597 4.802c.598 1.015 1.674 1.58 3.825 2.709l2 1.049c1.293.679 2.11 1.107 2.828 1.286ZM2.96 6.641l9.04 4.52l3.411-1.705l-8.886-5.078l-.103.054c-1.773.93-2.816 1.477-3.462 2.21Z"
                                                ></path>
                                            ) : (
                                                <path
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeLinecap="round"
                                                    strokeWidth="1.5"
                                                    d="m15.578 3.382l2 1.05c2.151 1.129 3.227 1.693 3.825 2.708C22 8.154 22 9.417 22 11.94v.117c0 2.525 0 3.788-.597 4.802c-.598 1.015-1.674 1.58-3.825 2.709l-2 1.049C13.822 21.539 12.944 22 12 22s-1.822-.46-3.578-1.382l-2-1.05c-2.151-1.129-3.227-1.693-3.825-2.708C2 15.846 2 14.583 2 12.06v-.117c0-2.525 0-3.788.597-4.802c.598-1.015 1.674-1.58 3.825-2.708l2-1.05C10.178 2.461 11.056 2 12 2s1.822.46 3.578 1.382ZM21 7.5l-4 2M12 12L3 7.5m9 4.5v9.5m0-9.5l4.5-2.25l.5-.25m0 0V13m0-3.5l-9.5-5"
                                                ></path>
                                            )}
                                        </svg>
                                        Accélérateurs
                                    </NavItem>
                                </Link>
                                <Link href="/mentors">
                                    <NavItem forPage="mentors" page={page}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-7 h-7"
                                            viewBox="0 0 24 24"
                                        >
                                            {page == "mentors" ? (
                                                <g fill="currentColor">
                                                    <circle
                                                        cx="9.001"
                                                        cy="6"
                                                        r="4"
                                                    ></circle>
                                                    <ellipse
                                                        cx="9.001"
                                                        cy="17.001"
                                                        rx="7"
                                                        ry="4"
                                                    ></ellipse>
                                                    <path d="M21 17c0 1.657-2.036 3-4.521 3c.732-.8 1.236-1.805 1.236-2.998c0-1.195-.505-2.2-1.239-3.001C18.962 14 21 15.344 21 17ZM18 6a3 3 0 0 1-4.029 2.82A5.688 5.688 0 0 0 14.714 6c0-1.025-.27-1.987-.742-2.819A3 3 0 0 1 18 6.001Z"></path>
                                                </g>
                                            ) : (
                                                <g
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                >
                                                    <circle
                                                        cx="9"
                                                        cy="6"
                                                        r="4"
                                                    ></circle>
                                                    <path
                                                        strokeLinecap="round"
                                                        d="M15 9a3 3 0 1 0 0-6"
                                                    ></path>
                                                    <ellipse
                                                        cx="9"
                                                        cy="17"
                                                        rx="7"
                                                        ry="4"
                                                    ></ellipse>
                                                    <path
                                                        strokeLinecap="round"
                                                        d="M18 14c1.754.385 3 1.359 3 2.5c0 1.03-1.014 1.923-2.5 2.37"
                                                    ></path>
                                                </g>
                                            )}
                                        </svg>
                                        Mentors
                                    </NavItem>
                                </Link>
                                <hr className="border-gray-300 dark:border-bgDark-550 m-5" />
                                <Link href="/community">
                                    <NavItem forPage="community" page={page}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-7 h-7"
                                            viewBox="0 0 24 24"
                                        >
                                            {page == "community" ? (
                                                <path
                                                    fill="currentColor"
                                                    d="M15.5 7.5a3.5 3.5 0 1 1-7 0a3.5 3.5 0 0 1 7 0Zm2.5 9c0 1.933-2.686 3.5-6 3.5s-6-1.567-6-3.5S8.686 13 12 13s6 1.567 6 3.5ZM7.122 5c.178 0 .35.017.518.05A4.977 4.977 0 0 0 7 7.5c0 .868.221 1.685.61 2.396c-.158.03-.32.045-.488.045c-1.414 0-2.561-1.106-2.561-2.47C4.561 6.106 5.708 5 7.122 5ZM5.447 18.986C4.88 18.307 4.5 17.474 4.5 16.5c0-.944.357-1.756.896-2.423C3.49 14.225 2 15.267 2 16.529c0 1.275 1.517 2.325 3.447 2.457ZM17 7.5c0 .868-.221 1.685-.61 2.396c.157.03.32.045.488.045c1.414 0 2.56-1.106 2.56-2.47c0-1.365-1.146-2.471-2.56-2.471c-.178 0-.35.017-.518.05c.407.724.64 1.56.64 2.45Zm1.553 11.486c1.93-.132 3.447-1.182 3.447-2.457c0-1.263-1.491-2.304-3.396-2.452c.54.667.896 1.479.896 2.423c0 .974-.38 1.807-.947 2.486Z"
                                                ></path>
                                            ) : (
                                                <g fill="currentColor">
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M12 1.25a4.75 4.75 0 1 0 0 9.5a4.75 4.75 0 0 0 0-9.5ZM8.75 6a3.25 3.25 0 1 1 6.5 0a3.25 3.25 0 0 1-6.5 0Z"
                                                        clipRule="evenodd"
                                                    ></path>
                                                    <path d="M18 3.25a.75.75 0 0 0 0 1.5c1.377 0 2.25.906 2.25 1.75S19.377 8.25 18 8.25a.75.75 0 0 0 0 1.5c1.937 0 3.75-1.333 3.75-3.25S19.937 3.25 18 3.25ZM6.75 4A.75.75 0 0 0 6 3.25c-1.937 0-3.75 1.333-3.75 3.25S4.063 9.75 6 9.75a.75.75 0 0 0 0-1.5c-1.376 0-2.25-.906-2.25-1.75S4.624 4.75 6 4.75A.75.75 0 0 0 6.75 4Z"></path>
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M12 12.25c-1.784 0-3.434.48-4.659 1.297c-1.22.814-2.091 2.02-2.091 3.453c0 1.433.871 2.64 2.091 3.453c1.225.816 2.875 1.297 4.659 1.297c1.784 0 3.434-.48 4.659-1.297c1.22-.814 2.091-2.02 2.091-3.453c0-1.433-.872-2.64-2.091-3.453c-1.225-.816-2.875-1.297-4.659-1.297ZM6.75 17c0-.776.472-1.57 1.423-2.204c.947-.631 2.298-1.046 3.827-1.046c1.53 0 2.88.415 3.827 1.046c.951.634 1.423 1.428 1.423 2.204c0 .776-.472 1.57-1.423 2.204c-.947.631-2.298 1.046-3.827 1.046c-1.53 0-2.88-.415-3.827-1.046C7.222 18.57 6.75 17.776 6.75 17Z"
                                                        clipRule="evenodd"
                                                    ></path>
                                                    <path d="M19.267 13.84a.75.75 0 0 1 .894-.573c.961.211 1.828.592 2.472 1.119c.643.526 1.117 1.25 1.117 2.114c0 .865-.474 1.588-1.117 2.114c-.644.527-1.51.908-2.472 1.119a.75.75 0 0 1-.322-1.466c.793-.173 1.426-.472 1.844-.814c.418-.342.567-.677.567-.953c0-.276-.149-.61-.567-.953c-.418-.342-1.051-.64-1.844-.814a.75.75 0 0 1-.572-.894ZM3.84 13.267a.75.75 0 1 1 .32 1.466c-.792.173-1.425.472-1.843.814c-.418.342-.567.677-.567.953c0 .276.149.61.567.953c.418.342 1.051.64 1.844.814a.75.75 0 0 1-.322 1.466c-.962-.211-1.828-.592-2.472-1.119C.724 18.088.25 17.364.25 16.5c0-.865.474-1.588 1.117-2.114c.644-.527 1.51-.908 2.472-1.119Z"></path>
                                                </g>
                                            )}
                                        </svg>
                                        Communauté
                                    </NavItem>
                                </Link>
                                <Link
                                    href="https://bit.ly/46gwnQo"
                                    target="_blank"
                                >
                                    <NavItem forPage="discord" page={page}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-7 h-7"
                                            viewBox="0 0 24 24"
                                        >
                                            {page == "discord" ? (
                                                <path
                                                    fill="currentColor"
                                                    d="M9 6a3 3 0 1 0 6 0a3 3 0 0 0-6 0ZM2.5 18a3 3 0 1 0 6 0a3 3 0 0 0-6 0Zm16 3a3 3 0 1 1 0-6a3 3 0 0 1 0 6ZM7.205 7.562a.75.75 0 0 0-.993-1.124A8.73 8.73 0 0 0 3.25 13a.75.75 0 0 0 1.5 0a7.23 7.23 0 0 1 2.455-5.438Zm10.583-1.124a.75.75 0 0 0-.993 1.124A7.23 7.23 0 0 1 19.25 13a.75.75 0 0 0 1.5 0a8.73 8.73 0 0 0-2.962-6.562Zm-7.601 13.584a.75.75 0 1 0-.374 1.452a8.773 8.773 0 0 0 4.374 0a.75.75 0 1 0-.374-1.452A7.267 7.267 0 0 1 12 20.25a7.31 7.31 0 0 1-1.813-.228Z"
                                                ></path>
                                            ) : (
                                                <g fill="currentColor">
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M15.75 6a3.75 3.75 0 1 0-7.5 0a3.75 3.75 0 0 0 7.5 0ZM12 3.75a2.25 2.25 0 1 1 0 4.5a2.25 2.25 0 0 1 0-4.5ZM9.25 18a3.75 3.75 0 1 0-7.5 0a3.75 3.75 0 0 0 7.5 0ZM5.5 15.75a2.25 2.25 0 1 1 0 4.5a2.25 2.25 0 0 1 0-4.5Zm13-1.5a3.75 3.75 0 1 1 0 7.5a3.75 3.75 0 0 1 0-7.5ZM20.75 18a2.25 2.25 0 1 0-4.5 0a2.25 2.25 0 0 0 4.5 0Z"
                                                        clipRule="evenodd"
                                                    ></path>
                                                    <path d="M7.205 7.562a.75.75 0 0 0-.993-1.124A8.73 8.73 0 0 0 3.25 13a.75.75 0 0 0 1.5 0a7.23 7.23 0 0 1 2.455-5.438Zm10.583-1.124a.75.75 0 0 0-.993 1.124A7.23 7.23 0 0 1 19.25 13a.75.75 0 0 0 1.5 0a8.73 8.73 0 0 0-2.962-6.562Zm-7.601 13.584a.75.75 0 1 0-.374 1.452a8.773 8.773 0 0 0 4.374 0a.75.75 0 1 0-.374-1.452A7.267 7.267 0 0 1 12 20.25a7.31 7.31 0 0 1-1.813-.228Z"></path>
                                                </g>
                                            )}
                                        </svg>
                                        Espaces privés
                                    </NavItem>
                                </Link>
                                {session && session.user && (
                                    <>
                                        <Link
                                            href={`/u/${
                                                session &&
                                                session.user &&
                                                session.user.pseudo
                                                    ? session.user.pseudo
                                                    : `believer-${session.user.userId}`
                                            }`}
                                        >
                                            <NavItem
                                                forPage="profile"
                                                page={page}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-7 h-7"
                                                    viewBox="0 0 24 24"
                                                >
                                                    {page == "profile" ? (
                                                        <g fill="currentColor">
                                                            <circle
                                                                cx="12"
                                                                cy="6"
                                                                r="4"
                                                            ></circle>
                                                            <path d="M20 17.5c0 2.485 0 4.5-8 4.5s-8-2.015-8-4.5S7.582 13 12 13s8 2.015 8 4.5Z"></path>
                                                        </g>
                                                    ) : (
                                                        <path
                                                            fill="currentColor"
                                                            fillRule="evenodd"
                                                            d="M12 1.25a4.75 4.75 0 1 0 0 9.5a4.75 4.75 0 0 0 0-9.5ZM8.75 6a3.25 3.25 0 1 1 6.5 0a3.25 3.25 0 0 1-6.5 0ZM12 12.25c-2.313 0-4.445.526-6.024 1.414C4.42 14.54 3.25 15.866 3.25 17.5v.102c-.001 1.162-.002 2.62 1.277 3.662c.629.512 1.51.877 2.7 1.117c1.192.242 2.747.369 4.773.369s3.58-.127 4.774-.369c1.19-.24 2.07-.605 2.7-1.117c1.279-1.042 1.277-2.5 1.276-3.662V17.5c0-1.634-1.17-2.96-2.725-3.836c-1.58-.888-3.711-1.414-6.025-1.414ZM4.75 17.5c0-.851.622-1.775 1.961-2.528c1.316-.74 3.184-1.222 5.29-1.222c2.104 0 3.972.482 5.288 1.222c1.34.753 1.961 1.677 1.961 2.528c0 1.308-.04 2.044-.724 2.6c-.37.302-.99.597-2.05.811c-1.057.214-2.502.339-4.476.339c-1.974 0-3.42-.125-4.476-.339c-1.06-.214-1.68-.509-2.05-.81c-.684-.557-.724-1.293-.724-2.601Z"
                                                            clipRule="evenodd"
                                                        ></path>
                                                    )}
                                                </svg>
                                                Profil
                                            </NavItem>
                                        </Link>
                                        {session &&
                                        session.user &&
                                        session.user.pro ? (
                                            <></>
                                        ) : (
                                            <>
                                                <hr className="border-gray-300 dark:border-bgDark-550 m-5" />

                                                <Link href={`/pricing`}>
                                                    <NavItem
                                                        forPage="pricing"
                                                        page={page}
                                                        highlighted
                                                    >
                                                        <Logo icon />
                                                        Believemy Premium
                                                    </NavItem>
                                                </Link>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Copyright */}
                            <div className="p-10 dark:text-white">
                                {new Date().getFullYear()} © Believemy
                                <div className="text-sm bold font-pacifico">
                                    For Believers.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <header
                className={`${
                    props.messenger
                        ? props.messenger
                        : `w-full z-50 transition-all duration-1000 ease-in-out ${
                              props.unstickyHeader ? "block" : "fixed"
                          } top-0 left-0 right-0 ${
                              isScrolled
                                  ? "background-blur"
                                  : props.transparentHeader
                                  ? ""
                                  : "bg-gradient-header"
                          }`
                }`}
            >
                <div className="m-container relative">
                    {/* Icon */}
                    <div
                        className={`absolute left-0 top-0 right-0 z-30 transition-all duration-1000 ${
                            isScrolled || props.messenger ? "p-5" : "p-7"
                        }`}
                    >
                        <div className="flex justify-center align-center">
                            <div className="block dark:hidden">
                                <div className="md:block hidden">
                                    <Link href="/">
                                        <Icon ratio="0.03" />
                                    </Link>
                                </div>
                                <div className="md:hidden block -mt-2">
                                    <Link href="/">
                                        <Icon ratio="0.025" />
                                    </Link>
                                </div>
                            </div>
                            <div className="hidden dark:block">
                                <div className="md:block hidden">
                                    <Link href="/">
                                        <Icon white ratio="0.03" />
                                    </Link>
                                </div>
                                <div className="md:hidden block -mt-2">
                                    <Link href="/">
                                        <Icon white ratio="0.025" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        className={`flex items-center justify-between transition-all duration-1000 ${
                            isScrolled || props.messenger ? "py-3" : "py-5"
                        } dark:text-white`}
                    >
                        {/* Search and category */}
                        <div className="flex items-center z-50">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-7 h-7 mr-4 hover:text-primary cursor-pointer"
                                viewBox="0 0 24 24"
                                onClick={() => setSideIsOpen(!sideIsOpen)}
                            >
                                <path
                                    fill="currentColor"
                                    fillRule="evenodd"
                                    d="M20.75 7a.75.75 0 0 1-.75.75H4a.75.75 0 0 1 0-1.5h16a.75.75 0 0 1 .75.75Zm0 5a.75.75 0 0 1-.75.75H4a.75.75 0 0 1 0-1.5h16a.75.75 0 0 1 .75.75Zm0 5a.75.75 0 0 1-.75.75H4a.75.75 0 0 1 0-1.5h16a.75.75 0 0 1 .75.75Z"
                                    clipRule="evenodd"
                                ></path>
                            </svg>
                            <Link href="/search" style={{ color: "inherit" }}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    className="w-7 h-7 mr-4 hover:text-primary cursor-pointer"
                                >
                                    <g
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                    >
                                        <circle
                                            cx="11.5"
                                            cy="11.5"
                                            r="9.5"
                                        ></circle>
                                        <path
                                            strokeLinecap="round"
                                            d="M18.5 18.5L22 22"
                                        ></path>
                                    </g>
                                </svg>
                            </Link>
                            {props.optionalMenu && props.optionalMenu}
                        </div>

                        {/* Buttons */}
                        {session && (
                            <div className="flex items-center space-x-4 z-50">
                                {/* Premium */}
                                {session &&
                                    session.user &&
                                    !session.user.pro && (
                                        <div className="lg:block hidden">
                                            <Link href="/pricing">
                                                {session.user.isTrialLocked ? (
                                                    <Button type="premium">
                                                        S'abonner
                                                    </Button>
                                                ) : (
                                                    <Button type="premium">
                                                        Essayer gratuitement
                                                    </Button>
                                                )}
                                            </Link>
                                        </div>
                                    )}
                                <div className="pl-5 relative">
                                    {/* Messages */}
                                    <Link
                                        href="/messenger"
                                        className="text-black dark:text-white"
                                    >
                                        {session.user.unreadedMessages > 0 && (
                                            <div className="absolute -top-3 -right-3 text-xs dark:text-white bg-red-400 rounded-full w-5 h-5 flex justify-center items-center mt-2 mr-2 z-10">
                                                {session.user.unreadedMessages}
                                            </div>
                                        )}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-7 h-7"
                                            viewBox="0 0 24 24"
                                        >
                                            <g fill="currentColor">
                                                <path
                                                    fillRule="evenodd"
                                                    d="M10.46 1.25h3.08c1.603 0 2.86 0 3.864.095c1.023.098 1.861.3 2.6.752a5.75 5.75 0 0 1 1.899 1.899c.452.738.654 1.577.752 2.6c.095 1.004.095 2.261.095 3.865v1.067c0 1.141 0 2.036-.05 2.759c-.05.735-.153 1.347-.388 1.913a5.75 5.75 0 0 1-3.112 3.112c-.805.334-1.721.408-2.977.43a10.81 10.81 0 0 0-.929.036c-.198.022-.275.054-.32.08c-.047.028-.112.078-.224.232c-.121.166-.258.396-.476.764l-.542.916c-.773 1.307-2.69 1.307-3.464 0l-.542-.916a10.605 10.605 0 0 0-.476-.764c-.112-.154-.177-.204-.224-.232c-.045-.026-.122-.058-.32-.08c-.212-.023-.49-.03-.93-.037c-1.255-.021-2.171-.095-2.976-.429A5.75 5.75 0 0 1 1.688 16.2c-.235-.566-.339-1.178-.389-1.913c-.049-.723-.049-1.618-.049-2.76v-1.066c0-1.604 0-2.86.095-3.865c.098-1.023.3-1.862.752-2.6a5.75 5.75 0 0 1 1.899-1.899c.738-.452 1.577-.654 2.6-.752C7.6 1.25 8.857 1.25 10.461 1.25ZM6.739 2.839c-.914.087-1.495.253-1.959.537A4.25 4.25 0 0 0 3.376 4.78c-.284.464-.45 1.045-.537 1.96c-.088.924-.089 2.11-.089 3.761v1c0 1.175 0 2.019.046 2.685c.045.659.131 1.089.278 1.441a4.25 4.25 0 0 0 2.3 2.3c.515.214 1.173.294 2.429.316h.031c.398.007.747.013 1.037.045c.311.035.616.104.909.274c.29.17.5.395.682.645c.169.232.342.525.538.856l.559.944a.52.52 0 0 0 .882 0l.559-.944c.196-.331.37-.624.538-.856c.182-.25.392-.476.682-.645c.293-.17.598-.24.909-.274c.29-.032.639-.038 1.037-.045h.032c1.255-.022 1.913-.102 2.428-.316a4.25 4.25 0 0 0 2.3-2.3c.147-.352.233-.782.278-1.441c.046-.666.046-1.51.046-2.685v-1c0-1.651 0-2.837-.089-3.762c-.087-.914-.253-1.495-.537-1.959a4.25 4.25 0 0 0-1.403-1.403c-.464-.284-1.045-.45-1.96-.537c-.924-.088-2.11-.089-3.761-.089h-3c-1.651 0-2.837 0-3.762.089Z"
                                                    clipRule="evenodd"
                                                ></path>
                                                <path d="M9 11a1 1 0 1 1-2 0a1 1 0 0 1 2 0Zm4 0a1 1 0 1 1-2 0a1 1 0 0 1 2 0Zm4 0a1 1 0 1 1-2 0a1 1 0 0 1 2 0Z"></path>
                                            </g>
                                        </svg>
                                    </Link>
                                </div>
                                <div className="relative">
                                    {/* Notifications */}
                                    {session.user.unreadedNotifications > 0 && (
                                        <div className="absolute -top-3 -right-3 text-xs text-white bg-red-400 rounded-full w-5 h-5 flex justify-center items-center mt-2 mr-2 z-10">
                                            {session.user.unreadedNotifications}
                                        </div>
                                    )}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-7 h-7 hover:text-primary cursor-pointer"
                                        viewBox="0 0 24 24"
                                        onClick={() => {
                                            if (!notificationsPanelIsOpen) {
                                                loadNotifications(true);
                                            }
                                            setNotificationsPanelIsOpen(
                                                !notificationsPanelIsOpen,
                                            );
                                            setMenuIsOpen(false);
                                        }}
                                    >
                                        <path
                                            fill="currentColor"
                                            fillRule="evenodd"
                                            d="M12 1.25A7.75 7.75 0 0 0 4.25 9v.704a3.53 3.53 0 0 1-.593 1.958L2.51 13.385c-1.334 2-.316 4.718 2.003 5.35c.755.206 1.517.38 2.284.523l.002.005C7.567 21.315 9.622 22.75 12 22.75s4.433-1.435 5.202-3.487l.002-.005a28.472 28.472 0 0 0 2.284-.523c2.319-.632 3.337-3.35 2.003-5.35l-1.148-1.723a3.53 3.53 0 0 1-.593-1.958V9A7.75 7.75 0 0 0 12 1.25Zm3.376 18.287a28.46 28.46 0 0 1-6.753 0c.711 1.021 1.948 1.713 3.377 1.713c1.429 0 2.665-.692 3.376-1.713ZM5.75 9a6.25 6.25 0 1 1 12.5 0v.704c0 .993.294 1.964.845 2.79l1.148 1.723a2.02 2.02 0 0 1-1.15 3.071a26.96 26.96 0 0 1-14.187 0a2.021 2.021 0 0 1-1.15-3.07l1.15-1.724a5.03 5.03 0 0 0 .844-2.79V9Z"
                                            clipRule="evenodd"
                                        ></path>
                                    </svg>
                                </div>
                                <div
                                    className="ml-5 flex items-center gap-1"
                                    style={{ lineHeight: 0 }}
                                >
                                    {/* Personal */}
                                    <div className="md:block hidden">
                                        <Image
                                            src={session.user.photo}
                                            alt={session.user.firstName}
                                            width={50}
                                            height={50}
                                            className="rounded-full object-cover cursor-pointer"
                                            onClick={() => {
                                                setMenuIsOpen(!menuIsOpen);
                                                setNotificationsPanelIsOpen(
                                                    false,
                                                );
                                            }}
                                            style={{
                                                width: 50,
                                                height: 50,
                                            }}
                                        />
                                    </div>
                                    <div className="md:hidden block">
                                        <Image
                                            src={session.user.photo}
                                            alt={session.user.firstName}
                                            width={50}
                                            height={50}
                                            className="rounded-full object-cover cursor-pointer"
                                            onClick={() => {
                                                setMenuIsOpen(!menuIsOpen);
                                                setNotificationsPanelIsOpen(
                                                    false,
                                                );
                                            }}
                                            style={{
                                                width: 35,
                                                height: 35,
                                            }}
                                        />
                                    </div>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={`h-4 w-4 text-dark dark:text-white cursor-pointer`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        onClick={() => {
                                            setMenuIsOpen(!menuIsOpen);
                                            setNotificationsPanelIsOpen(false);
                                        }}
                                    >
                                        {menuIsOpen ? (
                                            <path
                                                fill="currentColor"
                                                fillRule="evenodd"
                                                d="M11.512 8.43a.75.75 0 0 1 .976 0l7 6a.75.75 0 1 1-.976 1.14L12 9.987l-6.512 5.581a.75.75 0 1 1-.976-1.138l7-6Z"
                                                clipRule="evenodd"
                                            ></path>
                                        ) : (
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                            />
                                        )}
                                    </svg>
                                </div>
                            </div>
                        )}
                    </div>
                    {menuIsOpen && (
                        <div className="absolute right-0 text-sm shadow z-50">
                            <div className="rounded bg-white text-black w-48 overflow-hidden">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="text-white absolute -top-5 right-0 w-8 h-8"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        fill="currentColor"
                                        d="m12.37 8.165l6.43 6.63c.401.414.158 1.205-.37 1.205H5.57c-.528 0-.771-.79-.37-1.205l6.43-6.63a.499.499 0 0 1 .74 0Z"
                                    ></path>
                                </svg>
                                {links}
                            </div>
                        </div>
                    )}
                    {notificationsPanelIsOpen && (
                        <>
                            <div
                                className="absolute text-sm shadow z-50 md:block hidden"
                                style={{ right: 85 }}
                            >
                                {notificationsPanel}
                            </div>
                            <div
                                className="absolute text-sm shadow z-50 md:hidden block"
                                style={{ right: 70 }}
                            >
                                {notificationsPanel}
                            </div>
                        </>
                    )}
                </div>
            </header>
        </div>
    );
}
